import { FC, Fragment, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  TablePaginationProps,
  Typography,
} from '@mui/material';
import { NorthEast as NorthEastIcon, SouthWest as SouthWestIcon } from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';
import { TablePagination } from '../../components';
import { TransactionsFilter } from './components';

import {
  GetTransactionsDTO,
  useLazyGetCoinsQuery,
  useLazyGetProfileTransactionsQuery,
  useLazyGetProfileWalletsQuery,
  useLazyGetTransactionsQuery,
} from '../../api';
import { RouterPaths } from '../../router/paths';
import { localToUTC, UTCToDateTimeWithSecond } from '../../utils/dates';
import { PaymentMethod, TransactionDirection, TransactionStatus } from '../../enums';
import type { TransactionsComponentType } from './types';
import { formatAmount } from '../../utils';

type TransactionsProps = {
  componentType: TransactionsComponentType;
};

export const Transactions: FC<TransactionsProps> = ({ componentType }) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { profileUid } = useParams<'profileUid'>();

  // api
  const [
    getTransactions,
    {
      data: _transactions,
      isFetching: isTransactionsLoading,
      originalArgs: originalTransactionsArgs,
    },
  ] = useLazyGetTransactionsQuery();
  const [
    getProfileTransactions,
    {
      data: _profileTransactions,
      isFetching: isProfileTransactionsLoading,
      originalArgs: originalProfileTransactionsArgs,
      isUninitialized: isProfileTransactionsUninitialized,
    },
  ] = useLazyGetProfileTransactionsQuery();
  const [getProfileWallets, { data: profileWallets, isFetching: isProfileWalletsLoading }] =
    useLazyGetProfileWalletsQuery();
  const [getCoins, { data: coins, isFetching: isCoinsLoading }] = useLazyGetCoinsQuery();

  // variables
  const isDefaultType = componentType === 'default';
  const isForProfileType = componentType === 'forProfile';
  const isForWithdrawalsType = componentType === 'forWithdrawals';

  const transactions = {
    default: _transactions?.transactions,
    forProfile: _profileTransactions?.transactions,
    forWithdrawals: _transactions?.transactions,
  }[componentType];
  const total = {
    default: _transactions?.total,
    forProfile: _profileTransactions?.total,
    forWithdrawals: _transactions?.total,
  }[componentType];
  const totalPages = {
    default: _transactions?.total_pages,
    forProfile: _profileTransactions?.total_pages,
    forWithdrawals: _transactions?.total_pages,
  }[componentType];

  const isLoading =
    isTransactionsLoading ||
    isProfileTransactionsLoading ||
    isProfileWalletsLoading ||
    isCoinsLoading;

  const [isSearching, setSearchingStatus] = useState(false);

  // pagination logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const handleChangePage: TablePaginationProps['onPageChange'] = (e, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage: TablePaginationProps['onRowsPerPageChange'] = (e) => {
    setPage(0);
    setRowsPerPage(parseInt(e.target.value, 10));
  };

  // filters logic
  const [defaultFilterValues, setDefaultFilterValues] = useState<Partial<
    GetTransactionsDTO['params']
  > | null>(null);
  const [profileWalletUid, setProfileWalletUid] = useState<string>(state?.filterWalletUid ?? '');
  const [withdrawalsFilterValues, setWithdrawalsFilterValues] = useState<Partial<
    GetTransactionsDTO['params']
  > | null>(null);

  // fetch data according component type, pagination and filters
  useEffect(() => {
    if (isForProfileType && profileUid) {
      getProfileWallets({ profileUid });
    }
    if (isDefaultType) {
      getCoins();
    }
    setPage(0);
  }, [componentType]);

  useEffect(() => {
    if (isForProfileType && profileUid) {
      const args = isSearching
        ? {
            profileUid,
            params: {
              page: page + 1,
              page_size: rowsPerPage,
              wallet_uid: profileWalletUid,
            },
          }
        : { profileUid, params: { page: page + 1, page_size: rowsPerPage } };

      getProfileTransactions(args);
    } else if (isDefaultType) {
      const args = isSearching
        ? {
            params: {
              page: page + 1,
              page_size: rowsPerPage,

              ...defaultFilterValues,
              // @ts-ignore
              group: defaultFilterValues?.group === 'all' ? undefined : defaultFilterValues?.group,
              date_from: defaultFilterValues?.date_from
                ? localToUTC(defaultFilterValues.date_from, 'DD.MM.YYYY')
                : undefined,
              date_to: defaultFilterValues?.date_to
                ? localToUTC(defaultFilterValues.date_to, 'DD.MM.YYYY')
                : undefined,
            },
          }
        : { params: { page: page + 1, page_size: rowsPerPage } };

      getTransactions(args);
    } else if (isForWithdrawalsType) {
      const args = isSearching
        ? {
            params: {
              page: page + 1,
              page_size: rowsPerPage,
              direction: TransactionDirection.OUT,
              status: TransactionStatus.PENDING,
              payment_method: PaymentMethod.BANK,

              ...withdrawalsFilterValues,
              group:
                // @ts-ignore
                withdrawalsFilterValues?.group === 'all'
                  ? undefined
                  : withdrawalsFilterValues?.group,
            },
          }
        : {
            params: {
              page: page + 1,
              page_size: rowsPerPage,
              direction: TransactionDirection.OUT,
              status: TransactionStatus.PENDING,
              payment_method: PaymentMethod.BANK,
            },
          };

      getTransactions(args);
    }
  }, [page, rowsPerPage, isForWithdrawalsType]);

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('transactions.head-cells.id'),
    },
    {
      id: '2',
      label: t('transactions.head-cells.created-at'),
    },
    {
      id: '3',
      label: t('transactions.head-cells.status'),
    },
    {
      id: '4',
      label: t('transactions.head-cells.payment-method'),
    },
    {
      id: '5',
      label: t('transactions.head-cells.provider'),
    },
    {
      id: '6',
      label: t('transactions.head-cells.direction'),
    },
    {
      id: '7',
      label: t('transactions.head-cells.coin'),
    },
    {
      id: '8',
      label: t('transactions.head-cells.amount'),
      align: 'right',
    },
    {
      id: '9',
      label: t('transactions.head-cells.fee'),
      align: 'right',
    },
    {
      id: '10',
      label: t('transactions.head-cells.profile-name'),
    },
    {
      id: '11',
      label: t('transactions.head-cells.reference'),
    },
  ];

  const rows: Row[] | undefined = transactions?.map((it) => {
    const {
      id,
      created_at,
      status,
      payment_method,
      provider,
      direction,
      coin,
      amount,
      fee,
      profile_name,
      profile_uid,
      reference,
      merchant_deposit_request_uid,
    } = it;

    const cells: RowCell[] = [
      {
        name: 'id',
        value: id,
        component: (
          <MuiLink underline="hover" component={Link} to={`${RouterPaths.Transactions}/${id}`}>
            <Typography>{id}</Typography>
          </MuiLink>
        ),
        sx: { cursor: 'pointer' },
      },
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTimeWithSecond(value);
        },
      },
      {
        name: 'status',
        value: status,
        formatting: (value: string) => {
          return t(`common.transaction-status.${value}`);
        },
      },
      {
        name: 'payment_method',
        value: payment_method,
        formatting: (value: string) => {
          return merchant_deposit_request_uid
            ? `${t('transactions.merchant')} / ${t(`common.payment-method.${value}`)}`
            : t(`common.payment-method.${value}`);
        },
      },
      {
        name: 'provider',
        value: provider,
      },
      {
        name: 'direction',
        value: direction,
        component:
          direction === TransactionDirection.OUT ? (
            <Stack direction="row" gap="4px">
              <NorthEastIcon color="error" fontSize="small" />
              {t(`common.transaction-direction.${direction}`)}
            </Stack>
          ) : (
            <Stack direction="row" gap="4px">
              <SouthWestIcon color="success" fontSize="small" />
              {t(`common.transaction-direction.${direction}`)}
            </Stack>
          ),
      },
      {
        name: 'coin',
        value: (
          <Stack direction={'row'} alignItems={'center'}>
            {coin.code}
            {coin.blockchain && (
              <Box
                component="span"
                sx={{
                  backgroundColor: '#1976d2',
                  padding: '2px 4px',
                  borderRadius: '8px',
                  color: 'white',
                  ml: 1,
                  fontSize: 10,
                }}
              >
                {t(`common.blockchain.${coin.blockchain}`)}
              </Box>
            )}
          </Stack>
        ),
      },
      {
        name: 'amount',
        value: formatAmount(amount),
        align: 'right',
      },
      {
        name: 'fee',
        value: formatAmount(fee),
        align: 'right',
      },
      {
        name: 'profile_name',
        value: profile_name,
        component: (
          <MuiLink underline="hover" component={Link} to={`${RouterPaths.Profiles}/${profile_uid}`}>
            <Typography>{profile_name}</Typography>
          </MuiLink>
        ),
        sx: { cursor: 'pointer' },
      },
      {
        name: 'reference',
        value: reference,
        align: 'left',
      },
    ];

    return { cells };
  });

  // elements
  const Wrapper = {
    default: Fragment,
    forProfile: Stack,
    forWithdrawals: Fragment,
  }[componentType];
  const wrapperProps = {
    default: {},
    forProfile: { sx: { gap: 2 } },
    forWithdrawals: {},
  }[componentType];

  return (
    <Wrapper {...wrapperProps}>
      <TransactionsFilter
        componentType={componentType}
        defaultFilterValues={defaultFilterValues}
        setDefaultFilterValues={setDefaultFilterValues}
        profileWalletUid={profileWalletUid}
        setProfileWalletUid={setProfileWalletUid}
        withdrawalsFilterValues={withdrawalsFilterValues}
        setWithdrawalsFilterValues={setWithdrawalsFilterValues}
        setSearchingStatus={setSearchingStatus}
        setPage={setPage}
        originalTransactionsArgs={originalTransactionsArgs}
        originalProfileTransactionsArgs={originalProfileTransactionsArgs}
        isProfileTransactionsUninitialized={isProfileTransactionsUninitialized}
        profileWallets={profileWallets}
        coins={coins}
        getTransactions={getTransactions}
        getProfileTransactions={getProfileTransactions}
      />

      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} isLoading={isLoading} />
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={total ?? -1}
            totalPages={totalPages ?? 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isLoading={isLoading}
          />
        </Paper>
      </Grid>
    </Wrapper>
  );
};
